import Phaser from 'phaser';
import { App } from './App';
import {
  COLORS,
  FONTS,
  SPREADSHEET_WINDOW,
  TASKBAR,
  toHexColor,
} from '../config/gameConfig';
import { COPY } from '../content/copy';
import {
  SEED_INVOICES,
  agingBucketOf,
  totalByBucket,
  type AgingBucket,
  type AgingInvoice,
} from '../content/seed-invoices';
import type { BadgePayload, EventBus } from '../systems/EventBus';
import type { ARLedger } from '../systems/ARLedger';
import { Scrollable } from '../ui/Scrollable';

type Tab = 'open-invoices' | 'aging-report';

const BUCKET_ORDER: readonly AgingBucket[] = ['0-30', '31-60', '61-90', '90+'];

const bucketColor = (bucket: AgingBucket): number => {
  switch (bucket) {
    case '0-30': return 0x22c55e;
    case '31-60': return 0xfbbf24;
    case '61-90': return 0xf97316;
    case '90+': return 0xef4444;
  }
};

const bucketLabel = (bucket: AgingBucket): string => {
  switch (bucket) {
    case '0-30': return COPY.spreadsheet.bucket0_30;
    case '31-60': return COPY.spreadsheet.bucket31_60;
    case '61-90': return COPY.spreadsheet.bucket61_90;
    case '90+': return COPY.spreadsheet.bucket90plus;
  }
};

const fmtUsd = (n: number): string => `$${n.toLocaleString('en-US')}`;

export class AgingSpreadsheetApp extends App {
  private activeTab: Tab = 'open-invoices';
  private contentContainer!: Phaser.GameObjects.Container;
  private tabContainer!: Phaser.GameObjects.Container;
  private scrollable!: Scrollable;
  private highlightCustomer: string | null = null;
  private pulseActive = false;
  private pulseTimer: Phaser.Time.TimerEvent | null = null;

  private readonly onBeatTriggered = (p: { beatId: string }) => {
    if (p.beatId === 'beat-1-aging') {
      this.activeTab = 'aging-report';
      this.highlightCustomer = 'Acme Corp';
      // Beat-1 has no modal — opening the aging report IS the encounter, so
      // mark the term unlocked here. The "What is an aging bucket? ↗" link
      // in the spreadsheet body still lets the player pull up the teach panel.
      this.bus.emit('term:unlocked', { term: 'aging-bucket' });
      this.rerender();
    }
  };

  private readonly onLedgerResolved = (): void => {
    this.triggerPulse();
  };

  constructor(scene: Phaser.Scene, bus: EventBus, private readonly ledger?: ARLedger) {
    super(scene, bus, {
      x: SPREADSHEET_WINDOW.x,
      y: SPREADSHEET_WINDOW.y,
      width: SPREADSHEET_WINDOW.width,
      height: SPREADSHEET_WINDOW.height,
      title: COPY.spreadsheet.title,
      windowId: 'spreadsheet',
      titleBarHeight: SPREADSHEET_WINDOW.titleBarHeight,
    });
    this.renderBody();
    bus.on('beat:triggered', this.onBeatTriggered);
    bus.on('short-pay:resolved', this.onLedgerResolved);
    bus.on('dispute:resolved', this.onLedgerResolved);
    bus.on('ar-ledger:changed', this.onLedgerResolved);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
    this.emitBadge();
  }

  getBadge(): BadgePayload {
    return {
      label: 'AR Aging',
      count: 0,
      pulse: this.pulseActive,
      iconColor: 0x22c55e,
    };
  }

  protected onRestored(): void {
    if (this.pulseTimer) {
      this.pulseTimer.remove();
      this.pulseTimer = null;
    }
    this.pulseActive = false;
    this.emitBadge();
  }

  private emitBadge(): void {
    this.bus.emit('window:badge-changed', { windowId: this.options.windowId, badge: this.getBadge() });
  }

  private triggerPulse(): void {
    this.pulseActive = true;
    this.emitBadge();
    if (this.pulseTimer) this.pulseTimer.remove();
    this.pulseTimer = this.scene.time.delayedCall(TASKBAR.pulseHoldSpreadsheetMs, () => {
      this.pulseActive = false;
      this.pulseTimer = null;
      this.emitBadge();
    });
  }

  protected renderBody(): void {
    this.tabContainer = this.scene.add.container(0, 0);
    this.bodyContainer.add(this.tabContainer);
    this.contentContainer = this.scene.add.container(0, SPREADSHEET_WINDOW.tabBarHeight);
    this.bodyContainer.add(this.contentContainer);
    // Tab bar stays static; only the content area below it scrolls.
    this.scrollable = new Scrollable(this.scene, this, this.contentContainer, this.bodyContainer, {
      viewportYOffset: SPREADSHEET_WINDOW.tabBarHeight,
      viewportHeight: this.bodyHeight - SPREADSHEET_WINDOW.tabBarHeight,
    });
    this.rerender();
  }

  protected onResize(): void {
    this.scrollable?.resize(
      this.bodyWidth,
      this.bodyHeight - SPREADSHEET_WINDOW.tabBarHeight,
      0,
      SPREADSHEET_WINDOW.tabBarHeight,
    );
    this.rerender();
  }

  private currentInvoices(): readonly AgingInvoice[] {
    return this.ledger ? this.ledger.current : SEED_INVOICES;
  }

  private rerender(): void {
    if (!this.tabContainer || !this.contentContainer) return;
    this.tabContainer.removeAll(true);
    this.contentContainer.removeAll(true);
    this.renderTabs();
    if (this.activeTab === 'open-invoices') this.renderOpenInvoices();
    else this.renderAgingReport();
    this.refreshChildDepths();
  }

  private renderTabs(): void {
    const tabs: { id: Tab; label: string }[] = [
      { id: 'open-invoices', label: COPY.spreadsheet.tabOpenInvoices },
      { id: 'aging-report', label: COPY.spreadsheet.tabAgingReport },
    ];
    let x = SPREADSHEET_WINDOW.paddingX;
    for (const t of tabs) {
      const isActive = t.id === this.activeTab;
      const labelColor = isActive ? COLORS.textPrimary : COLORS.textMuted;
      const label = this.scene.add.text(x, SPREADSHEET_WINDOW.tabBarHeight / 2, t.label, {
        fontFamily: FONTS.ui,
        fontSize: '13px',
        color: toHexColor(labelColor),
        fontStyle: isActive ? 'bold' : 'normal',
      }).setOrigin(0, 0.5);
      label.setInteractive({ useHandCursor: true });
      label.on('pointerdown', () => {
        if (this.activeTab !== t.id) {
          this.activeTab = t.id;
          this.rerender();
        }
      });
      this.tabContainer.add(label);

      if (isActive) {
        const underline = this.scene.add.rectangle(
          x, SPREADSHEET_WINDOW.tabBarHeight - 2,
          label.width, 2, COLORS.buttonPrimary,
        ).setOrigin(0, 0);
        this.tabContainer.add(underline);
      }
      x += label.width + 24;
    }

    // "What is an aging bucket? ↗" — pull-not-push educational hook on
    // the aging-report tab. Right-aligned in the tab strip.
    if (this.activeTab === 'aging-report') {
      const help = this.scene.add.text(
        this.bodyWidth - SPREADSHEET_WINDOW.paddingX,
        SPREADSHEET_WINDOW.tabBarHeight / 2,
        'What is an aging bucket? ↗',
        {
          fontFamily: FONTS.ui,
          fontSize: '12px',
          color: toHexColor(COLORS.markedTermUnderline),
          fontStyle: 'italic',
        },
      ).setOrigin(1, 0.5).setInteractive({ useHandCursor: true });
      help.on('pointerdown', () => this.bus.emit('teach:requested', { term: 'aging-bucket' }));
      this.tabContainer.add(help);
    }

    const divider = this.scene.add.rectangle(
      0, SPREADSHEET_WINDOW.tabBarHeight - 1,
      this.bodyWidth, 1, COLORS.emailSeparator,
    ).setOrigin(0, 0);
    this.tabContainer.add(divider);
  }

  private renderOpenInvoices(): void {
    const colWidths = [150, 100, 100, 80, 100, 70, 90];
    const headers = [
      COPY.spreadsheet.columnCustomer,
      COPY.spreadsheet.columnInvoice,
      COPY.spreadsheet.columnAmount,
      COPY.spreadsheet.columnIssued,
      COPY.spreadsheet.columnTerms,
      COPY.spreadsheet.columnAging,
      COPY.spreadsheet.columnStatus,
    ];
    let hx = SPREADSHEET_WINDOW.paddingX;
    for (let i = 0; i < headers.length; i++) {
      const h = this.scene.add.text(hx, SPREADSHEET_WINDOW.headerHeight / 2, headers[i], {
        fontFamily: FONTS.ui,
        fontSize: SPREADSHEET_WINDOW.headerFontSize,
        color: toHexColor(COLORS.textMuted),
        fontStyle: 'bold',
      }).setOrigin(0, 0.5);
      this.contentContainer.add(h);
      hx += colWidths[i];
    }
    const headerDivider = this.scene.add.rectangle(
      0, SPREADSHEET_WINDOW.headerHeight - 1,
      this.bodyWidth, 1, COLORS.emailSeparator,
    ).setOrigin(0, 0);
    this.contentContainer.add(headerDivider);

    // Render every invoice — Scrollable wraps contentContainer so overflow scrolls.
    let ry = SPREADSHEET_WINDOW.headerHeight;
    for (const inv of this.currentInvoices()) {
      const isHighlight = inv.customer === this.highlightCustomer && inv.agingDays > 90;
      if (isHighlight) {
        const hl = this.scene.add.rectangle(0, ry, this.bodyWidth, SPREADSHEET_WINDOW.rowHeight, 0xef4444, 0.15)
          .setOrigin(0, 0);
        this.contentContainer.add(hl);
      }
      // Per-row clickable hit area → opens DunningModal pre-loaded with this customer.
      const hit = this.scene.add
        .rectangle(0, ry, this.bodyWidth, SPREADSHEET_WINDOW.rowHeight, 0x000000, 0)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerover', () => hit.setFillStyle(COLORS.windowTitleBar, 0.25));
      hit.on('pointerout', () => hit.setFillStyle(0x000000, 0));
      hit.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (!this.scrollable?.viewportContainsPointer(pointer.x, pointer.y)) return;
        this.bus.emit('dunning:open-for', {
          customer: inv.customer,
          bucket: agingBucketOf(inv.agingDays),
        });
      });
      this.contentContainer.add(hit);

      let cx = SPREADSHEET_WINDOW.paddingX;
      const cells = [
        inv.customer,
        inv.invoiceNumber,
        fmtUsd(inv.amount),
        inv.issuedDate,
        inv.terms,
        `${inv.agingDays}d`,
        inv.status,
      ];
      for (let i = 0; i < cells.length; i++) {
        const t = this.scene.add.text(cx, ry + SPREADSHEET_WINDOW.rowHeight / 2, cells[i], {
          fontFamily: FONTS.ui,
          fontSize: SPREADSHEET_WINDOW.rowFontSize,
          color: toHexColor(isHighlight ? COLORS.textPrimary : COLORS.emailSubject),
        }).setOrigin(0, 0.5);
        this.contentContainer.add(t);
        cx += colWidths[i];
      }
      ry += SPREADSHEET_WINDOW.rowHeight;
    }

    this.scrollable?.setContentHeight(ry + 8);
  }

  private renderAgingReport(): void {
    const totals = totalByBucket(this.currentInvoices());
    const maxAmount = Math.max(...BUCKET_ORDER.map((b) => totals[b]));
    const areaWidth = this.bodyWidth - SPREADSHEET_WINDOW.paddingX * 2;
    const barMaxWidth = areaWidth - 260;
    const barHeight = 40;
    const rowGap = 20;

    let y = 24;
    for (const bucket of BUCKET_ORDER) {
      const amount = totals[bucket];
      const barW = maxAmount === 0 ? 0 : Math.round((amount / maxAmount) * barMaxWidth);
      const color = bucketColor(bucket);

      const label = this.scene.add.text(
        SPREADSHEET_WINDOW.paddingX,
        y + barHeight / 2,
        bucketLabel(bucket),
        {
          fontFamily: FONTS.ui,
          fontSize: SPREADSHEET_WINDOW.bucketLabelFontSize,
          color: toHexColor(COLORS.textPrimary),
          fontStyle: 'bold',
        },
      ).setOrigin(0, 0.5);
      this.contentContainer.add(label);

      const bar = this.scene.add.rectangle(
        SPREADSHEET_WINDOW.paddingX + 120,
        y,
        Math.max(barW, 1),
        barHeight,
        color,
      ).setOrigin(0, 0);
      this.contentContainer.add(bar);

      const amountText = this.scene.add.text(
        SPREADSHEET_WINDOW.paddingX + 120 + Math.max(barW, 1) + 12,
        y + barHeight / 2,
        fmtUsd(amount),
        {
          fontFamily: FONTS.ui,
          fontSize: SPREADSHEET_WINDOW.bucketFontSize,
          color: toHexColor(COLORS.textPrimary),
        },
      ).setOrigin(0, 0.5);
      this.contentContainer.add(amountText);

      if (bucket === '90+' && this.highlightCustomer === 'Acme Corp') {
        const note = this.scene.add.text(
          SPREADSHEET_WINDOW.paddingX + 120,
          y + barHeight + 2,
          'Acme Corp — $42,000 · 90+',
          {
            fontFamily: FONTS.ui,
            fontSize: '11px',
            color: toHexColor(COLORS.accentDanger),
            fontStyle: 'italic',
          },
        );
        this.contentContainer.add(note);
      }

      y += barHeight + rowGap;
    }

    const total = BUCKET_ORDER.reduce((s, b) => s + totals[b], 0);
    const summary = this.scene.add.text(
      SPREADSHEET_WINDOW.paddingX,
      y + 16,
      `${COPY.spreadsheet.totalLabel} ${fmtUsd(total)} across ${this.currentInvoices().length} invoices`,
      {
        fontFamily: FONTS.ui,
        fontSize: '12px',
        color: toHexColor(COLORS.textMuted),
      },
    );
    this.contentContainer.add(summary);
    this.scrollable?.setContentHeight(y + 16 + summary.height + 8);
  }

  private teardown(): void {
    if (this.pulseTimer) {
      this.pulseTimer.remove();
      this.pulseTimer = null;
    }
    this.bus.off('beat:triggered', this.onBeatTriggered);
    this.bus.off('short-pay:resolved', this.onLedgerResolved);
    this.bus.off('dispute:resolved', this.onLedgerResolved);
    this.scrollable?.destroy();
  }
}
